import { useQuery } from '@tanstack/react-query';

import { getTopupCodes } from '@/services/topup-code';

export interface TopupCode {
  topup_code_id: string;
  // The string an order spends. Not sent by backends from before checkout
  // listed top-ups as a way to pay.
  code?: string;
  classes_left: number;
  // Absent on a backend without waitlists: read it through getHeldCredits.
  credits_held?: number;
  expires_at: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'EXPIRED' | 'DELETED' | 'FULLY_USED';
  source_type: 'PACK' | 'SUBSCRIPTION';
  topup?: {
    name: string;
    image: string | null;
  };
  topup_order_item?: {
    quantity: number;
    original_price: number;
    discounted_price: number;
    currency: string;
  } | null;
  source_class_pack_code?: {
    class_pack_code_id: string;
    class_pack?: {
      name: string;
      image: string | null;
    };
  } | null;
  source_subscription_code?: {
    subscription_code_id: string;
    subscription?: {
      name: string;
      image: string | null;
    };
  } | null;
}

export default function useTopupCodes({
  userId,
  email,
}: { userId?: string; email?: string } = {}) {
  const { data, isLoading, isSuccess, error, refetch } = useQuery({
    queryKey: ['topupCodes', userId, email],
    queryFn: () => getTopupCodes({ user_id: userId, email }),
    enabled: !!userId || !!email,
    refetchOnMount: true,
  });

  return {
    data,
    isLoading,
    // A 2xx whose body lacked the expected key resolves with no data and no
    // error, so an unreadable answer has to be caught alongside a rejected one.
    hasFailedRead: !!error || (isSuccess && !data),
    error,
    refetch,
  };
}
